import type { RegisterApplicationConfig, SingleSpaCustomEventDetail } from 'single-spa'
import { assertIsSingleSpaCustomEvent, SINGLE_SPA_BEFORE_MOUNT_ROUTING_EVENT } from '../../common/single-spa-events'
import { getTopbarAPI } from '../../services/topbar'
import { canShowTopBar } from '../bootstrap-helpers'
import type { ShellHook } from './models'
import type { CustomProps } from '../../common'
import { findRegisteredApplicationsByNames } from './registered-applications-utils'
import { getFeatureFlagValue } from '../../services/feature-flags'
import { FeatureFlagsVariations } from '../../services/feature-flags/models'
import { getViewsApplication } from '../../experiences'
import { type shellEvents, ShellNamespace } from '../../services/shell-namespace'
import { getEventBus } from '../../services/namespaces/event-bus'

const SETTINGS_APPLICATION_NAME = 'goto/settings'

const setTopbarVisibility = (show: boolean) => {
  const topbarAPI = getTopbarAPI()
  show ? topbarAPI.show() : topbarAPI.hide()
}

export const applicationHasHideTopbarConfig = (
  registeredApplications: readonly RegisterApplicationConfig<CustomProps>[],
): boolean =>
  registeredApplications.some(registeredApplication => {
    const { customProps } = registeredApplication
    return !!(customProps as CustomProps)?.shellConfig?.hideTopbar
  })

const applicationisInViews = (appName: string) => getViewsApplication().some(viewsApp => viewsApp.name === appName)

const mountedAppShouldBeCheckedForVisibility = (appName: string) =>
  appName === SETTINGS_APPLICATION_NAME || applicationisInViews(appName)

const updateTopbarVisibility = (mountedApps: string[] = []) => {
  const registeredApplications = findRegisteredApplicationsByNames(mountedApps)
  if (
    !mountedApps.some(appName => mountedAppShouldBeCheckedForVisibility(appName)) &&
    registeredApplications?.length === 0
  ) {
    return
  }
  const showTopbar = canShowTopBar() && !applicationHasHideTopbarConfig(registeredApplications)
  setTopbarVisibility(showTopbar)
}

const handleBeforeMountRoutingEvent = (event: CustomEvent<SingleSpaCustomEventDetail>) => {
  assertIsSingleSpaCustomEvent(event)
  const mountedApps = (event.detail?.appsByNewStatus?.MOUNTED as string[]) ?? []
  updateTopbarVisibility(mountedApps)
}

export const initializeTopBarVisibility: ShellHook = async () => {
  globalThis.addEventListener(SINGLE_SPA_BEFORE_MOUNT_ROUTING_EVENT, handleBeforeMountRoutingEvent)

  if (getFeatureFlagValue(FeatureFlagsVariations.SHELL_LOAD_VIEWS_ON_APP_START, false)) {
    setTopbarVisibility(canShowTopBar())

    const { applicationReady } = getEventBus().subscribeTo<typeof ShellNamespace, typeof shellEvents>(ShellNamespace)
    const onApplicationReady = () => {
      updateTopbarVisibility()
    }
    applicationReady.on(onApplicationReady)
  }
}
